import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Mail,
  RotateCw,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/seo/SEO";
import { api, getApiErrorStatus } from "@/services/api/client";
import { useHomeProjectRedirect } from "@/hooks/useHomeProjectRedirect";
import {
  getPublicAuthChallengeFailureMessage,
  getPublicAuthChallengeToken,
  isPublicAuthChallengeFailure,
} from "@/services/api/publicAuthChallenge";
import { useAuth, useAuthActions } from "@/stores/auth/auth-store";
import type {
  ApiResponse,
  LegacyResendVerificationResponse,
  RegistrationSelectedPlan,
  ResendVerificationRequest,
  ResendVerificationResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from "@/types";
import { TIER_DISPLAY } from "@/types/billing";
import {
  clearPendingRegistrationEmail,
  normalizePendingRegistrationEmail,
  readPendingRegistrationEmail,
  savePendingRegistrationEmail,
} from "@/utils/pendingRegistrationEmail";
import {
  clearPendingRegistrationSelectedPlan,
  getPendingRegistrationSelectedPlanPath,
  parsePendingRegistrationSelectedPlan,
  readPendingRegistrationSelectedPlan,
  savePendingRegistrationSelectedPlan,
} from "@/utils/pendingRegistrationSelectedPlan";
import {
  parsePendingRegistrationAccountKind,
  readPendingRegistrationAccountKind,
  savePendingRegistrationAccountKind,
  SPONSORSHIP_ACCOUNT_KIND,
} from "@/utils/pendingRegistrationAccountContext";

type VerificationState =
  | "missing-token"
  | "verifying"
  | "verified"
  | "failed"
  | "challenge-failed";

type ResendState =
  | "idle"
  | "sending"
  | "sent"
  | "failed"
  | "challenge-failed"
  | "unavailable";

const GENERIC_VERIFY_FAILURE =
  "This verification link could not be confirmed. It may be expired, already used, or already verified. Request a new link or sign in to continue.";

const RESEND_ACCEPTED_MESSAGE =
  "If an unverified account exists for that email, a new verification link will be sent.";

const getResendMessageAccessibility = (
  resendState: ResendState,
): {
  role: "status" | "alert";
  live: "polite" | "assertive";
  className: string;
} => {
  switch (resendState) {
    case "sent":
      return {
        role: "status",
        live: "polite",
        className:
          "rounded-md border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400",
      };
    case "failed":
    case "challenge-failed":
    case "unavailable":
      return {
        role: "alert",
        live: "assertive",
        className:
          "rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive",
      };
    case "idle":
    case "sending":
    default:
      return {
        role: "status",
        live: "polite",
        className:
          "rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground",
      };
  }
};

const getSelectedPlanName = (
  selectedPlan: RegistrationSelectedPlan | undefined,
): string | null => {
  if (!selectedPlan) {
    return null;
  }

  return TIER_DISPLAY[selectedPlan.tier].name;
};

const renderSelectedPlanSummary = (
  selectedPlan: RegistrationSelectedPlan | undefined,
) => {
  if (!selectedPlan) {
    return null;
  }

  return (
    <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium">Selected plan</span>
        {selectedPlan.tier !== "free" && (
          <Badge variant="secondary">
            {selectedPlan.billingInterval === "annual" ? "Annual" : "Monthly"}
          </Badge>
        )}
      </div>
      <p className="mt-1 text-muted-foreground">
        {TIER_DISPLAY[selectedPlan.tier].name} plan
      </p>
    </div>
  );
};

const getVerifiedSuccessCopy = (
  verification: VerifyEmailResponse | null,
  fallbackSelectedPlan: RegistrationSelectedPlan | undefined,
): string => {
  const planName = getSelectedPlanName(
    verification?.selectedPlan ?? fallbackSelectedPlan,
  );

  switch (verification?.nextAction) {
    case "checkout":
      return planName
        ? `Email verification is complete. Sign in to continue to checkout for the ${planName} plan.`
        : "Email verification is complete. Sign in to continue to checkout.";
    case "free":
      return "Email verification is complete. Sign in to start your TerraMatrix trial.";
    case "login":
    default:
      return planName
        ? `Email verification is complete. Sign in to continue with your ${planName} plan.`
        : "Email verification is complete. Sign in to continue.";
  }
};

const withLoginEmailParam = (path: string, email: string): string => {
  const normalizedEmail = normalizePendingRegistrationEmail(email);
  if (!normalizedEmail) {
    return path;
  }

  const [pathname, query = ""] = path.split("?");
  const params = new URLSearchParams(query);
  params.set("email", normalizedEmail);
  return `${pathname}?${params.toString()}`;
};

const isVerifiedResponse = (
  response: ApiResponse<VerifyEmailResponse>,
): boolean => {
  if (response.success !== true) {
    return false;
  }

  const verification = response.data;
  return verification?.verified === true || verification?.status === "verified";
};

const isResendAcceptedResponse = (
  response: ApiResponse<
    ResendVerificationResponse | LegacyResendVerificationResponse
  >,
): boolean => {
  if (response.success !== true || !response.data) {
    return false;
  }

  const resend = response.data;
  if ("sent" in resend) {
    return resend.sent === true && resend.status === "accepted";
  }

  return (
    resend.accepted === true &&
    (resend.status === undefined || resend.status === "accepted")
  );
};

const verifyEmailRequests = new Map<
  string,
  Promise<ApiResponse<VerifyEmailResponse>>
>();

function verifyEmailOnce(
  token: string,
): Promise<ApiResponse<VerifyEmailResponse>> {
  const existingRequest = verifyEmailRequests.get(token);
  if (existingRequest) {
    return existingRequest;
  }

  const request = getPublicAuthChallengeToken("verify-email")
    .then((publicAuthChallengeToken) =>
      api.post<VerifyEmailResponse, VerifyEmailRequest>(
        "/auth/verify-email",
        publicAuthChallengeToken
          ? { token, publicAuthChallengeToken }
          : { token },
      ),
    )
    .finally(() => {
      verifyEmailRequests.delete(token);
    });

  verifyEmailRequests.set(token, request);
  return request;
}

export const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const { logout } = useAuthActions();
  const { performRedirect } = useHomeProjectRedirect();
  const token = useMemo(
    () => searchParams.get("token")?.trim() ?? "",
    [searchParams],
  );
  const querySelectedPlan = useMemo(
    () =>
      parsePendingRegistrationSelectedPlan(
        searchParams.get("selectedTier"),
        searchParams.get("billingInterval"),
      ),
    [searchParams],
  );
  const queryEmail = normalizePendingRegistrationEmail(
    searchParams.get("email") ?? "",
  );
  const accountKind =
    parsePendingRegistrationAccountKind(searchParams.get("accountKind")) ??
    readPendingRegistrationAccountKind();
  const isSponsorshipRegistration = accountKind === SPONSORSHIP_ACCOUNT_KIND;
  const initialEmail = queryEmail || readPendingRegistrationEmail();
  const [verificationState, setVerificationState] = useState<VerificationState>(
    token ? "verifying" : "missing-token",
  );
  const [verificationResult, setVerificationResult] =
    useState<VerifyEmailResponse | null>(null);
  const [resendState, setResendState] = useState<ResendState>("idle");
  const [resendEmail, setResendEmail] = useState(initialEmail);
  const [handoffState, setHandoffState] = useState<
    "idle" | "switching-account" | "returning-to-workspace"
  >("idle");
  const [handoffError, setHandoffError] = useState("");
  const currentSessionEmail = normalizePendingRegistrationEmail(user?.email ?? "");
  const verifiedAccountEmail = normalizePendingRegistrationEmail(
    verificationResult?.email ?? "",
  );
  const hasFixedResendEmail = initialEmail !== "";
  const selectedPlan =
    isSponsorshipRegistration
      ? undefined
      : verificationResult?.selectedPlan ??
        querySelectedPlan ??
        readPendingRegistrationSelectedPlan();
  const loginPath = getPendingRegistrationSelectedPlanPath(
    "/login",
    selectedPlan,
  );
  const verifiedLoginPath = withLoginEmailParam(
    loginPath,
    verifiedAccountEmail,
  );
  const isDifferentVerifiedAccount =
    currentSessionEmail !== "" &&
    verifiedAccountEmail !== "" &&
    currentSessionEmail !== verifiedAccountEmail;

  useLayoutEffect(() => {
    if (isSponsorshipRegistration) {
      clearPendingRegistrationSelectedPlan();
      savePendingRegistrationAccountKind(SPONSORSHIP_ACCOUNT_KIND);
    }
    if (queryEmail) {
      savePendingRegistrationEmail(queryEmail);
    }
    if (!isSponsorshipRegistration && querySelectedPlan) {
      savePendingRegistrationSelectedPlan(querySelectedPlan);
    }

    if (typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    const hasSensitiveQueryParam =
      url.searchParams.has("token") || url.searchParams.has("email");

    if (!hasSensitiveQueryParam) {
      return;
    }

    url.searchParams.delete("token");
    url.searchParams.delete("email");
    window.history.replaceState(
      window.history.state,
      document.title,
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, [isSponsorshipRegistration, queryEmail, querySelectedPlan, token]);

  useEffect(() => {
    if (isSponsorshipRegistration) {
      clearPendingRegistrationSelectedPlan();
      return;
    }

    if (verificationResult?.selectedPlan) {
      savePendingRegistrationSelectedPlan(verificationResult.selectedPlan);
    }
  }, [isSponsorshipRegistration, verificationResult]);

  useEffect(() => {
    let isMounted = true;

    if (!token) {
      setVerificationState("missing-token");
      return () => {
        isMounted = false;
      };
    }

    setVerificationState("verifying");
    setVerificationResult(null);

    verifyEmailOnce(token)
      .then((response) => {
        if (!isMounted) {
          return;
        }

        if (isVerifiedResponse(response)) {
          setVerificationResult(response.data ?? null);
          clearPendingRegistrationEmail();
          setVerificationState("verified");
          return;
        }

        setVerificationState("failed");
      })
      .catch((error: unknown) => {
        if (isMounted) {
          setVerificationState(
            isPublicAuthChallengeFailure(error) ? "challenge-failed" : "failed",
          );
        }
      });

    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleResend = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = resendEmail.trim();

    if (!email) {
      return;
    }

    setResendState("sending");

    try {
      const publicAuthChallengeToken = await getPublicAuthChallengeToken(
        "resend-verification",
      );
      const response = await api.post<
        ResendVerificationResponse,
        ResendVerificationRequest
      >(
        "/auth/resend-verification",
        publicAuthChallengeToken
          ? { email, publicAuthChallengeToken }
          : { email },
      );

      setResendState(isResendAcceptedResponse(response) ? "sent" : "failed");
    } catch (error) {
      if (isPublicAuthChallengeFailure(error)) {
        setResendState("challenge-failed");
        return;
      }

      const status = getApiErrorStatus(error);
      if (status === 401 || status === 403) {
        setResendState("unavailable");
        return;
      }

      setResendState("failed");
    }
  };

  const resendMessage = (() => {
    switch (resendState) {
      case "sent":
        return RESEND_ACCEPTED_MESSAGE;
      case "unavailable":
        return "Verification resend is not available from this page yet. Sign in or contact support if the link has expired.";
      case "challenge-failed":
        return getPublicAuthChallengeFailureMessage();
      case "failed":
        return "We could not process that resend request. Please try again later.";
      case "idle":
      case "sending":
      default:
        return "";
    }
  })();
  const resendMessageAccessibility = getResendMessageAccessibility(resendState);

  const handleSwitchAccount = async () => {
    setHandoffState("switching-account");
    setHandoffError("");

    try {
      await logout();
      navigate(verifiedLoginPath, { replace: true });
    } catch {
      setHandoffError(
        "We could not sign you out automatically. Please try again.",
      );
      setHandoffState("idle");
    }
  };

  const handleReturnToWorkspace = async () => {
    setHandoffState("returning-to-workspace");
    setHandoffError("");

    try {
      await performRedirect();
    } catch {
      setHandoffError(
        "We could not return you to your workspace automatically. Please try again.",
      );
      setHandoffState("idle");
    }
  };

  const renderAuthenticatedHandoff = (signOutLabel: string) => {
    const primaryLabel = isDifferentVerifiedAccount
      ? "Sign out and continue with verified account"
      : signOutLabel;

    return (
      <div className="space-y-3">
        <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 text-sm text-muted-foreground">
          {isDifferentVerifiedAccount ? (
            <p>
              This link verified{" "}
              <span className="font-medium">{verifiedAccountEmail}</span>, but
              you are still signed in as{" "}
              <span className="font-medium">{currentSessionEmail}</span>. Sign
              out first to continue with the verified account.
            </p>
          ) : currentSessionEmail ? (
            <p>
              You are still signed in as{" "}
              <span className="font-medium">{currentSessionEmail}</span>. Sign
              out first if you need to continue with a different account.
            </p>
          ) : (
            <p>
              You are still signed in. Sign out first if you need to continue
              with a different account.
            </p>
          )}
        </div>
      {handoffError && (
        <div
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {handoffError}
        </div>
      )}
      <Button
        type="button"
        className="w-full"
        onClick={() => {
          void handleSwitchAccount();
        }}
        disabled={handoffState !== "idle"}
      >
        {handoffState === "switching-account"
          ? "Signing out..."
          : primaryLabel}
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={() => {
          void handleReturnToWorkspace();
        }}
        disabled={handoffState !== "idle"}
      >
        {handoffState === "returning-to-workspace"
          ? "Returning to workspace..."
          : "Return to workspace"}
      </Button>
      </div>
    );
  };

  const renderResendForm = () => (
    <form onSubmit={handleResend} className="space-y-3">
      <div className="space-y-2">
        <label
          htmlFor="verify-email-resend-email"
          className="text-sm font-medium"
        >
          Email
        </label>
        <Input
          id="verify-email-resend-email"
          type="email"
          value={resendEmail}
          onChange={(event) => {
            setResendEmail(event.target.value);
            if (resendState !== "sending") {
              setResendState("idle");
            }
          }}
          placeholder="you@example.com"
          disabled={resendState === "sending" || hasFixedResendEmail}
          autoComplete="email"
        />
        {hasFixedResendEmail && (
          <p className="text-sm text-muted-foreground">
            This email came from your registration and is required for this
            verification link.
          </p>
        )}
      </div>
      <Button
        type="submit"
        variant="secondary"
        className="w-full"
        disabled={resendState === "sending" || resendEmail.trim() === ""}
      >
        {resendState === "sending" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <RotateCw className="mr-2 h-4 w-4" />
        )}
        Send verification link
      </Button>
      {resendMessage && (
        <div
          role={resendMessageAccessibility.role}
          aria-live={resendMessageAccessibility.live}
          aria-atomic="true"
          className={resendMessageAccessibility.className}
        >
          {resendMessage}
        </div>
      )}
    </form>
  );

  const content = (() => {
    switch (verificationState) {
      case "verifying":
        return {
          icon: <Loader2 className="h-5 w-5 animate-spin text-primary" />,
          title: "Verifying Email",
          description: "Confirming your verification link.",
          body: (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Checking link
            </div>
          ),
        };
      case "verified":
        return {
          icon: <CheckCircle2 className="h-5 w-5 text-emerald-600" />,
          title: "Email Verified",
          description: "Your account is ready for sign in.",
          body: (
            <div className="space-y-4">
              <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 px-3 py-2 rounded-md text-sm"
              >
                {getVerifiedSuccessCopy(verificationResult, selectedPlan)}
              </div>
              {isAuthenticated ? (
                renderAuthenticatedHandoff("Sign out and continue to sign in")
              ) : (
                <Button asChild className="w-full">
                  <Link to={verifiedLoginPath}>Sign in</Link>
                </Button>
              )}
            </div>
          ),
        };
      case "missing-token":
        return {
          icon: <Mail className="h-5 w-5 text-primary" />,
          title: "Verification Link Needed",
          description: "Request a new verification link.",
          body: (
            <div className="space-y-4">
              <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className="bg-destructive/10 border border-destructive/20 text-destructive px-3 py-2 rounded-md text-sm"
              >
                This verification link is missing required information.
              </div>
              {renderResendForm()}
              {isAuthenticated ? (
                renderAuthenticatedHandoff("Sign out and go to sign in")
              ) : (
                <Button asChild variant="ghost" className="w-full">
                  <Link to={loginPath}>Back to sign in</Link>
                </Button>
              )}
            </div>
          ),
        };
      case "challenge-failed":
        return {
          icon: <AlertCircle className="h-5 w-5 text-destructive" />,
          title: "Request Not Verified",
          description: "Refresh and try again.",
          body: (
            <div className="space-y-4">
              <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className="bg-destructive/10 border border-destructive/20 text-destructive px-3 py-2 rounded-md text-sm"
              >
                {getPublicAuthChallengeFailureMessage()}
              </div>
              {renderResendForm()}
              {isAuthenticated ? (
                renderAuthenticatedHandoff("Sign out and go to sign in")
              ) : (
                <Button asChild variant="ghost" className="w-full">
                  <Link to={loginPath}>Back to sign in</Link>
                </Button>
              )}
            </div>
          ),
        };
      case "failed":
      default:
        return {
          icon: <AlertCircle className="h-5 w-5 text-destructive" />,
          title: "Verification Link Not Confirmed",
          description: "Request a new link or sign in.",
          body: (
            <div className="space-y-4">
              <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className="bg-destructive/10 border border-destructive/20 text-destructive px-3 py-2 rounded-md text-sm"
              >
                {GENERIC_VERIFY_FAILURE}
              </div>
              {renderResendForm()}
              {isAuthenticated ? (
                renderAuthenticatedHandoff("Sign out and go to sign in")
              ) : (
                <Button asChild variant="ghost" className="w-full">
                  <Link to={loginPath}>Back to sign in</Link>
                </Button>
              )}
            </div>
          ),
        };
    }
  })();

  return (
    <>
      <SEO
        title="Verify Your TerraMatrix Email"
        description="Verify your TerraMatrix account email address."
        canonical="/verify-email"
        noindex={true}
      />
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            {content.icon}
            <CardTitle>{content.title}</CardTitle>
          </div>
          <CardDescription>{content.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isSponsorshipRegistration && (
            <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 text-sm">
              <span className="font-medium">Account type</span>
              <p className="mt-1 text-muted-foreground">Sub-account</p>
            </div>
          )}
          {renderSelectedPlanSummary(selectedPlan)}
          {content.body}
        </CardContent>
      </Card>
    </>
  );
};
